import { Button, Collapse, Grid } from '@arco-design/web-react';
import { TextStyle, useBlock, useFocusIdx } from '@bay-email/editor';
import React from 'react';
import { useTableSelection } from './Operation/TableSelectionContext';
import MENU_CONFIG from './Operation/tableMenuConfig';
import { ContainerBackgroundColor } from '../../attributes/ContainerBackgroundColor';
import { LineHeight } from '../../attributes/LineHeight';
import { Padding } from '../../attributes/Padding';
import { LetterSpacing } from '../../attributes/LetterSpacing';
import { Width } from '../../attributes/Width';
import { CollapseWrapper } from '../../attributes/CollapseWrapper';
import { BorderWidth } from '../../attributes/BorderWidth';
import { BorderStyle } from '../../attributes/BorderStyle';
import { BorderColor } from '../../attributes/BorderColor';
import { BorderWidthControl } from '../shared/BorderWidthControl';
import { ColorPickerField, NumberField, TextField } from '@extensions';
import { pixelAdapter } from '../../adapter';
import { t } from '@bay-email/core';
import BayTooltip from '@bay-ui/BayTooltip';
import { AttachedListingSection } from '../shared/AttachedListingSection';
import {
	collectAdvancedTableCellContent,
	usesListingMergeTags,
} from '../shared/listingAttachment';
import { useClearAttachedListingWhenUnused } from '../shared/useClearAttachedListingWhenUnused';

export function AdvancedTable() {
	const { focusIdx } = useFocusIdx();
	const { focusBlock, change } = useBlock();
	const {
		hasSelection,
		canMerge,
		mergeCells,
		deleteSelectedRows,
		deleteSelectedColumns,
		insertRowUp,
		insertRowDown,
		insertColumnLeft,
		insertColumnRight,
	} = useTableSelection();

	const tableSource: any[][] = focusBlock?.data?.value?.tableSource || [];

	// Advanced tables keep each cell's HTML in tableSource, so gather every cell's
	// content to detect listing merge tags (mirrors the basic Table block, which
	// reads its single data.value.content string). One listing per table.
	const cellContent = React.useMemo(
		() => collectAdvancedTableCellContent(focusBlock),
		[focusBlock],
	);
	const showListingSettings = React.useMemo(
		() => usesListingMergeTags(cellContent),
		[cellContent],
	);
	useClearAttachedListingWhenUnused(focusBlock, showListingSettings);
	const defaultActiveKey = React.useMemo(
		() => (showListingSettings ? ['-1', '0', '1', '2'] : ['0', '1', '2']),
		[showListingSettings],
	);

	const ActionIcon = ({
		actionID,
	}: {
		actionID: Exclude<keyof typeof MENU_CONFIG, 'setCellBg'>;
	}) => (
		<div className='d-flex justify-content-center'>
			<span
				className='d-inline-flex align-items-center justify-content-center'
				style={{
					width: 20,
					height: 20,
				}}
				dangerouslySetInnerHTML={{
					__html: MENU_CONFIG[actionID]?.icon || '',
				}}
			/>
			<span>{MENU_CONFIG[actionID]?.text}</span>
		</div>
	);

	return (
		<CollapseWrapper
			isChevron
			iconPosition='right'
			defaultActiveKey={defaultActiveKey}
			style={{ padding: 0 }}
		>
			{showListingSettings && (
				<Collapse.Item
					name='-1'
					header={t('Listing Settings')}
					contentStyle={{ padding: 0 }}
				>
					<AttachedListingSection observedValues={[cellContent]} />
				</Collapse.Item>
			)}
			<Collapse.Item
				name='0'
				header={t('Content Block Settings')}
				contentStyle={{ padding: 0 }}
			>
				<div className='p-3'>
					<TextStyle variation='strong'>{t('Row Actions')}</TextStyle>
					<Grid.Row
						gutter={8}
						className='my-2'
					>
						<Grid.Col span={12}>
							<Button
								size='small'
								long
								disabled={!hasSelection}
								onClick={insertRowUp}
							>
								<ActionIcon actionID='insertRowUp' />
							</Button>
						</Grid.Col>
						<Grid.Col span={12}>
							<Button
								size='small'
								long
								disabled={!hasSelection}
								onClick={insertRowDown}
							>
								<ActionIcon actionID='insertRowDown' />
							</Button>
						</Grid.Col>
					</Grid.Row>
					<Grid.Row
						gutter={8}
						className='mb-3'
					>
						<Grid.Col span={24}>
							<Button
								size='small'
								long
								disabled={!hasSelection}
								onClick={deleteSelectedRows}
							>
								<ActionIcon actionID='deleteRow' />
							</Button>
						</Grid.Col>
					</Grid.Row>

					<TextStyle variation='strong'>{t('Column Actions')}</TextStyle>
					<Grid.Row
						gutter={8}
						className='my-2'
					>
						<Grid.Col span={12}>
							<Button
								size='small'
								long
								disabled={!hasSelection}
								onClick={insertColumnLeft}
							>
								<ActionIcon actionID='insertColumnLeft' />
							</Button>
						</Grid.Col>
						<Grid.Col span={12}>
							<Button
								size='small'
								long
								disabled={!hasSelection}
								onClick={insertColumnRight}
							>
								<ActionIcon actionID='insertColumnRight' />
							</Button>
						</Grid.Col>
					</Grid.Row>
					<Grid.Row
						gutter={8}
						className='mb-3'
					>
						<Grid.Col span={24}>
							<Button
								size='small'
								long
								disabled={!hasSelection}
								onClick={deleteSelectedColumns}
							>
								<ActionIcon actionID='deleteColumn' />
							</Button>
						</Grid.Col>
					</Grid.Row>

					<TextStyle variation='strong'>{t('Merge Actions')}</TextStyle>
					<Grid.Row
						gutter={8}
						className='mt-2'
					>
						<Grid.Col span={24}>
							<BayTooltip
								disabled={canMerge}
								overlay={t('Click and drag to select cells in the table')}
							>
								<div className='cursor-pointer'>
									<Button
										size='small'
										long
										disabled={!canMerge}
										onClick={mergeCells}
									>
										<ActionIcon actionID='mergeCells' />
									</Button>
								</div>
							</BayTooltip>
						</Grid.Col>
					</Grid.Row>
				</div>
			</Collapse.Item>
			<Collapse.Item
				name='1'
				header={t('Dimension')}
				contentStyle={{ padding: 0 }}
			>
				<div className='p-3'>
					<Grid.Row
						gutter={16}
						className='mb-3'
					>
						<Grid.Col span={12}>
							<div style={{ paddingRight: '8px' }}>
								<Width
									autoEnabled
									config={pixelAdapter}
								/>
							</div>
						</Grid.Col>
					</Grid.Row>
					<Padding />
				</div>
			</Collapse.Item>

			<Collapse.Item
				name='2'
				header={t('Styling')}
				contentStyle={{ padding: 0 }}
			>
				<div className='p-3'>
					<ContainerBackgroundColor title={t('Background color')} />
					<LetterSpacing
						label={t('Letter Spacing (px)')}
						config={pixelAdapter}
					/>
					<LineHeight />

					{/* Table border and Cell border color in two-column layout */}
					<Grid.Row gutter={16}>
						<Grid.Col span={24}>
							<div>
								<TextStyle variation='strong'>{t('Table Border')}</TextStyle>
								<Grid.Row gutter={16}>
									<Grid.Col span={16}>
										<BorderColor secondary />
									</Grid.Col>
									<Grid.Col span={8}>
										<BorderStyle secondary />
									</Grid.Col>
								</Grid.Row>
								<BorderWidthControl />
							</div>
						</Grid.Col>
					</Grid.Row>
					{/* Cell border colour. Cell background colour is set per-cell via the
					    rich-text toolbar's paint-bucket tool, not here. */}
					<Grid.Row gutter={16}>
						<Grid.Col span={12}>
							<ColorPickerField
								label={t('Cell border color')}
								name={`${focusIdx}.attributes.cellBorderColor`}
							/>
						</Grid.Col>
					</Grid.Row>
					{/* Cell padding in half-width layout */}
					<Grid.Row gutter={16}>
						<Grid.Col span={12}>
							<NumberField
								label={t('Cell padding (px)')}
								name={`${focusIdx}.attributes.cellPadding`}
								config={pixelAdapter}
								max={20}
								min={0}
								step={1}
							/>
						</Grid.Col>
					</Grid.Row>
				</div>
			</Collapse.Item>
		</CollapseWrapper>
	);
}
